import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Paper, Grid, Button, Checkbox, FormControl, Select, MenuItem } from '@material-ui/core';
import { Tab, Tabs } from '@material-ui/core';
import { useStyles } from './styles';
import { observer } from 'mobx-react-lite';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { GoogleProfile } from './types';
import TabPanel from 'components/TabPanel';
import { useLocation } from 'react-router-dom';
import { useStylesSecond } from './styles';
import WebAssetIcon from '@material-ui/icons/WebAsset';
import { getFieldConfig } from '../../../utils/fieldConfigUtils';

// Add type definitions for field configuration
// interface FieldConfig {
//   destKey?: string;
//   destLabel?: string;
//   sourseKey?: string;
//   sourceKey?: string;
//   sourseLabel?: string;
//   sourceLabel?: string;
//   isFromPreference?: boolean;
//   defaultValue?: string;
// }
function useQuerys() {
  return new URLSearchParams(useLocation().search);
}

export const InfoTable: FC = observer(() => {
  const classes = {
    ...useStyles(),
    ...useStylesSecond(),
  };
  const [googleProfile, setGoogleProfile] = useState<GoogleProfile | undefined>(undefined);
  const [infoAttributeMappings, setInfoAttributeMappings] = useState<{ [key: string]: { [key: string]: string } }>({
    people: {},
    group: {},
    department: {},
  });
  const {
    AppStore: { appConnect, GoogleKeyGenrates, getAttributePreferences, AttributePreferences },
    // dashboardStore: { getAppStatistic },
    notificationStore: { openNotification },
  } = useStore();
  const { control, handleSubmit, setValue } = useForm<any>({
    defaultValues: {
      is_sync: false,
    },
  });

  useEffect(() => {
    document.title = 'EasyIdentity: App: InfoDetail';
  }, []);

  const googleConnectedReq = useQuery('googleConnectedReq', () => appConnect(), {
    onError: () => openNotification('error', `Can't connect with google`),
  });

  const getAttributePreferencesReq = useQuery('getAttributePreferencesReq', () => getAttributePreferences('google'), {
    onError: () => openNotification('error', `Can't get attribute`),
  });

  const infoData = getAttributePreferencesReq?.data?.data;

  const getAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) return [];
    return infoData.attributes.filter((attr: any) => attr.entity === entity);
  };

  const getGoogleAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) return [];

    // Use Map to avoid duplicates and store the best label for each key
    const sourceKeyToLabel = new Map();

    // First, collect from API data for this specific entity
    infoData.attributes
      .filter((attr: any) => attr.entity === entity)
      .forEach((attr: any) => {
        // Add from default
        if (attr.default?.sourseKey || attr.default?.sourceKey) {
          const key = attr.default.sourseKey || attr.default.sourceKey;
          const label = attr.default.sourseLabel || attr.default.sourceLabel || key;
          if (key && key !== null && key !== 'null') {
            sourceKeyToLabel.set(key, label);
          }
        }
        // Add from preference
        if (attr.preference?.sourseKey || attr.preference?.sourceKey) {
          const key = attr.preference.sourseKey || attr.preference.sourceKey;
          const label = attr.preference.sourseLabel || attr.preference.sourceLabel || key;
          if (key && key !== null && key !== 'null') {
            sourceKeyToLabel.set(key, label);
          }
        }
      });

    // Add entity-specific Google Workspace attributes
    let entitySpecificAttributes: { key: string; label: string }[] = [];

    if (entity === 'people') {
      entitySpecificAttributes = [
        { key: 'primaryEmail', label: 'Primary Email' },
        { key: 'userPrincipalName', label: 'User Principal Name' },
        { key: 'givenName', label: 'First Name' },
        { key: 'familyName', label: 'Last Name' },
        { key: 'displayName', label: 'Display Name' },
        { key: 'password', label: 'Password' },
        { key: 'officeLocation', label: 'Office Location' },
        { key: 'recoveryPhone', label: 'Recovery Phone' },
        { key: 'addresses', label: 'Addresses' },
        { key: 'orgUnitPath', label: 'Organization Unit Path' },
        { key: 'suspended', label: 'Suspended' },
        { key: 'languages', label: 'Languages' },
        { key: 'mail', label: 'Mail' },
        { key: 'phoneNumber', label: 'Phone Number' },
        { key: 'jobTitle', label: 'Job Title' },
        { key: 'department', label: 'Department' },
        { key: 'manager', label: 'Manager' },
      ];
    } else if (entity === 'group') {
      entitySpecificAttributes = [
        { key: 'name', label: 'Group Name' },
        { key: 'description', label: 'Description' },
        { key: 'email', label: 'Group Email' },
        { key: 'mail', label: 'Mail' },
        { key: 'groupType', label: 'Group Type' },
        { key: 'members', label: 'Members' },
        { key: 'aliases', label: 'Aliases' },
        { key: 'settings', label: 'Settings' },
      ];
    } else if (entity === 'department') {
      entitySpecificAttributes = [
        { key: 'name', label: 'Department Name' },
        { key: 'description', label: 'Description' },
        { key: 'parentOrgUnitPath', label: 'Parent Organization Unit Path' },
        { key: 'orgUnitPath', label: 'Organization Unit Path' },
        { key: 'blockInheritance', label: 'Block Inheritance' },
        { key: 'includeInGlobalAddressList', label: 'Include in Global Address List' },
      ];
    }

    // Add entity-specific attributes only if not already present
    entitySpecificAttributes.forEach((attr) => {
      if (!sourceKeyToLabel.has(attr.key)) {
        sourceKeyToLabel.set(attr.key, attr.label);
      }
    });

    // Convert Map to array and sort
    const options = Array.from(sourceKeyToLabel.entries())
      .map(([sourseKey, sourseLabel]) => ({
        sourseKey,
        sourseLabel,
        entity,
      }))
      .sort((a, b) => a.sourseLabel.localeCompare(b.sourseLabel));
    return options;
  };

  const isAttributeEditable = (destKey: string) => {
    return true; // All fields are editable except Google fixed fields
  };

  const isGoogleAttributeFixed = (sourseKey: string) => {
    const fixedGoogleFields = [
      'primaryEmail',
      'userPrincipalName', // Email - fixed
      'password',
      'id',
      'email',
      // Password - fixed
    ];
    return fixedGoogleFields.includes(sourseKey);
  };

  useEffect(() => {
    if (googleConnectedReq?.data) {
      googleConnectedReq.data.forEach((item: any) => {
        if (item.provider === 'google') {
          setGoogleProfile(item);
        }
      });
    }
  }, [googleConnectedReq?.data]);

  useEffect(() => {
    if (googleProfile) {
      setValue('is_sync', googleProfile.is_sync);
    }
  }, [googleProfile, setValue]);

  const GoogleKeyGenratesreq = useMutation((data: any) => GoogleKeyGenrates(data), {
    mutationKey: 'GoogleKeyGenratesreq',
    onSuccess: (data) => {
      openNotification('success', 'Saved!!');
      googleConnectedReq.refetch();
      localStorage.removeItem('open');
    },
    onError: (error) => {
      openNotification('error', `Failed to save`);
    },
  });

  const AttributePreferencesReq = useMutation((data: any) => AttributePreferences(data), {
    mutationKey: 'AttributePreferencesReq',
    onSuccess: (data) => {
      openNotification('success', 'Attribute mappings saved!');
      getAttributePreferencesReq.refetch(); // Refresh the data
      setInfoAttributeMappings({
        people: {},
        group: {},
        department: {},
      });
    },
    onError: (error) => {
      openNotification('error', 'Failed to save attribute mappings');
    },
  });

  const onSubmit = (data: any) => {
    const payload = {
      is_sync: data.is_sync,
      // manager_id: data.manager_id?.map((user: any) => user.user_id || user.id).filter(Boolean) || []
    };
    GoogleKeyGenratesreq.mutate(payload);
  };

  function a11yProps(index: string) {
    return {
      id: `wrapped-tab-${index}`,
      'aria-controls': `wrapped-tabpanel-${index}`,
    };
  }
  const query = useQuerys();
  const queryValue = query.get('value');
  const defaultTabs = (Array.isArray(queryValue) ? queryValue[0] : queryValue) ?? 'one';

  const [value, setValues] = useState<string>(defaultTabs);

  const handleChange = (event: ChangeEvent<unknown>, newValue: string) => {
    setValues(newValue);
  };

  const getCurrentInfoTabMappings = () => {
    if (value === 'one') return infoAttributeMappings.people || {};
    if (value === 'two') return infoAttributeMappings.group || {};
    if (value === 'three') return infoAttributeMappings.department || {};
    return {};
  };

  const handleInfoAttributeMapping = (destKey: string, sourseKey: string) => {
    const tabKey = value === 'one' ? 'people' : value === 'two' ? 'group' : 'department';
    setInfoAttributeMappings((prev) => {
      const newMappings = { ...prev };

      // Clear the selected value from ALL other fields first to prevent duplicates
      if (sourseKey && sourseKey !== 'none') {
        Object.keys(newMappings[tabKey]).forEach((key) => {
          if (key !== destKey && newMappings[tabKey][key] === sourseKey) {
            newMappings[tabKey][key] = '';
          }
        });
      }

      // Set the new mapping
      newMappings[tabKey] = {
        ...newMappings[tabKey],
        [destKey]: sourseKey === 'none' ? '' : sourseKey,
      };
      return newMappings;
    });
  };

  const saveInfoAttributeMappings = () => {
    const allEntities = ['people', 'group', 'department'];
    const allRequiredFields: any[] = [];

    allEntities.forEach((entity) => {
      const entityAttributes = getAttributesByEntity(entity);
      const requiredFields = entityAttributes
        .filter((attr: any) => attr.required)
        .filter((attr: any) => {
          const fieldConfig = getFieldConfig(attr);
          if (!fieldConfig.destKey) return false;
          const tabKey = entity;
          return !infoAttributeMappings[tabKey]?.[fieldConfig.destKey];
        });

      allRequiredFields.push(...requiredFields.map((field: any) => ({ ...field, entity })));
    });

    if (allRequiredFields.length > 0) {
      const fieldNames = allRequiredFields
        .map((field: any) => {
          const fieldConfig = getFieldConfig(field);
          return `${fieldConfig.destLabel || 'Unknown'} (${field.entity})`;
        })
        .join(', ');
      openNotification('error', `Please map all required fields: ${fieldNames}`);
      return;
    }

    const payload = {
      app_name: 'google',
      attributes: [] as any[],
    };

    const allMappings = {
      people: infoAttributeMappings.people || {},
      group: infoAttributeMappings.group || {},
      department: infoAttributeMappings.department || {},
    };

    Object.entries(allMappings).forEach(([entity, mappings]) => {
      const allEntityAttributes = getAttributesByEntity(entity);
      allEntityAttributes.forEach((originalAttr: any) => {
        const fieldConfig = getFieldConfig(originalAttr);
        let destKey = fieldConfig.destKey;

        // Handle special case where destKey might be stored in a different property
        if (!destKey && originalAttr.default) {
          // Check if any property in default looks like a destKey
          Object.keys(originalAttr.default).forEach((key) => {
            if (
              key !== 'destKey' &&
              key !== 'destLabel' &&
              key !== 'sourseKey' &&
              key !== 'sourseLabel' &&
              key !== 'defaultValue'
            ) {
              // This might be the destKey stored in a different property
              destKey = key;
            }
          });
        }

        if (destKey) {
          const sourseKey = mappings[destKey] || '';
          const selectedGoogleAttr = sourseKey
            ? getGoogleAttributesByEntity(entity).find((attr: any) => attr.sourseKey === sourseKey)
            : null;
          const attributeItem = {
            type: originalAttr.type,
            entity,
            hidden: originalAttr.hidden,
            required: originalAttr.required,
            default: originalAttr.default, // Include original default
            preference: {
              destKey,
              destLabel: fieldConfig.destLabel || '',
              sourseKey: sourseKey || '', // Include empty string to clear mapping
              sourseLabel: selectedGoogleAttr?.sourseLabel || sourseKey || '',
            },
          };
          payload.attributes.push(attributeItem);
        }
      });
    });
    AttributePreferencesReq.mutate(payload);
  };

  // Initialize mappings from API data
  useEffect(() => {
    if (infoData?.attributes) {
      const mappings = {
        people: {} as { [key: string]: string },
        group: {} as { [key: string]: string },
        department: {} as { [key: string]: string },
      };

      infoData.attributes.forEach((attr: any) => {
        // Use utility function to get the correct configuration
        const fieldConfig = getFieldConfig(attr);
        if (fieldConfig.destKey) {
          // Get the source key from preference if it exists, otherwise from default
          let sourceKey = '';
          if (fieldConfig.isFromPreference && (fieldConfig.sourseKey || fieldConfig.sourceKey)) {
            sourceKey = fieldConfig.sourseKey || fieldConfig.sourceKey || '';
          } else if (attr.default && (attr.default.sourseKey || attr.default.sourceKey)) {
            sourceKey = attr.default.sourseKey || attr.default.sourceKey || '';
          }

          // Handle null values
          if (sourceKey === null || sourceKey === 'null') {
            sourceKey = '';
          }
          mappings[attr.entity as keyof typeof mappings][fieldConfig.destKey] = sourceKey;
        }
      });
      setInfoAttributeMappings(mappings);
    }
  }, [infoData]);

  const isGoogleAttributeDisabled = (googleKey: string, currentLdapKey: string) => {
    // Allow all selections - no restrictions on duplicates
    return false;
  };

  const handleNext = () => {
    const currentEntity = value === 'one' ? 'people' : value === 'two' ? 'group' : 'department';
    const requiredFields = getAttributesByEntity(currentEntity)
      .filter((attr: any) => attr.required)
      .filter((attr: any) => {
        const fieldConfig = getFieldConfig(attr);
        if (!fieldConfig.destKey) return false;
        return !getCurrentInfoTabMappings()[fieldConfig.destKey];
      });

    if (requiredFields.length > 0) {
      const fieldNames = requiredFields
        .map((field: any) => {
          const fieldConfig = getFieldConfig(field);
          return fieldConfig.destLabel || 'Unknown';
        })
        .join(', ');
      openNotification('error', `Please map required fields: ${fieldNames}`);
      return;
    }

    if (value === 'one') setValues('two');
    else if (value === 'two') setValues('three');
  };

  const handleBack = () => {
    if (value === 'three') setValues('two');
    else if (value === 'two') setValues('one');
  };

  const isLastTab = value === 'three';
  const isFirstTab = value === 'one';

  // const getAppStatisticReq = useQuery('getAppStatisticReq', () => getAppStatistic(), {
  //   onError: () => openNotification('error', `Can't get statistic`),
  // });
  // const appStatistics = getAppStatisticReq?.data?.data?.totals
  useAppPreloader(GoogleKeyGenratesreq.isLoading || googleConnectedReq.isFetching || AttributePreferencesReq.isLoading);
  return (
    <>
      {/* <Container maxWidth='xl' className={classes.container}>
        <Grid className={classes.infoContainer} container alignItems="stretch" spacing={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.new_users_created_this_week ?? 0}</p>
              <p className={classes.title}>New Users<br />This Week</p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.new_users_created_this_month ?? 0}</p>
              <p className={classes.title}>New Users<br />This Month</p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_synced_this_week ?? 0}</p>
              <p className={classes.title}>Users Synced<br />This Week</p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_synced_this_month ?? 0}</p>
              <p className={classes.title}>Users Synced<br />This Month</p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_deleted_this_week ?? 0}</p>
              <p className={classes.title}>Users Deleted<br />This Week</p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_deleted_this_month ?? 0}</p>
              <p className={classes.title}>Users Deleted<br />This Month</p>
            </Paper>
          </Grid>
        </Grid>
      </Container> */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper className={classes.paper}>
          <div className={classes.leftCol}>
            <Grid item xs={12} className={classes.btnContainer}>
              <Button className={classes.submitBtn} type='submit' variant='contained'>
                Save changes
              </Button>
            </Grid>
          </div>
          <TableContainer>
            <Table className={`${classes.border} ${classes.boxes}`} aria-label='simple table'>
              <TableBody>
                <TableRow>
                  <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                    Automatically Sync All Users, Groups and Departments with Google Workspace (bi-directional)
                  </TableCell>
                  <TableCell className={classes.border}>
                    <Controller
                      key='is_sync'
                      name='is_sync'
                      control={control}
                      render={({ field }) => (
                        <Grid container className={classes.row}>
                          <Checkbox
                            className={classes.tableCheck}
                            checked={field.value === true}
                            onChange={(event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
                              field.onChange(checked ? true : false)
                            }
                          />
                        </Grid>
                      )}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </form>
      <div className={classes.mappingHeader}>
        <h2 className={classes.mappingTitle}>Configure Attribute Mapping</h2>
        <p className={classes.mappingSubText}>
          Map Easyidentity attributes to their corresponding Google Workspace fields.
        </p>
      </div>
      <Grid item xl className={classes.tabsPanels}>
        <Paper className={classes.tabsPaper}>
          <Tabs
            value={value}
            className={classes.tabs}
            TabIndicatorProps={{ style: { color: 'transparent', background: 'transparent' } }}
            onChange={handleChange}
          >
            <Tab value='one' wrapped label={<span>Users</span>} {...a11yProps('one')} className={classes.label} />
            <Tab value='two' wrapped label={<span>Groups</span>} {...a11yProps('two')} className={classes.label} />
            <Tab
              value='three'
              wrapped
              label={<span>Departments</span>}
              {...a11yProps('three')}
              className={classes.label}
            />
          </Tabs>

          <div className={classes.tabBox}>
            <TabPanel value={value} index='one'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='user attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>Google Attributes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('people')
                      .map((attr: any, index: number) => {
                        const fieldConfig = getFieldConfig(attr);
                        // Show row if it has either destKey or sourseKey (for display purposes)
                        if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                              {fieldConfig.destLabel || 'Unknown'}
                              {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                              {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                            </TableCell>
                            <TableCell className={`${classes.border} ${classes.cellLink}`}>
                              {isGoogleAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                                <FormControl fullWidth>
                                  <Select
                                    key={`${fieldConfig.destKey}-${
                                      getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'
                                    }`}
                                    value={getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || ''}
                                    onChange={(e) =>
                                      handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                    }
                                    displayEmpty
                                    className={classes.selectBlock}
                                  >
                                    <MenuItem value=''>None</MenuItem>
                                    {getGoogleAttributesByEntity('people').map((option: any, optIndex: number) => (
                                      <MenuItem
                                        key={optIndex}
                                        value={option.sourseKey}
                                        disabled={
                                          isGoogleAttributeFixed(option.sourseKey) ||
                                          isGoogleAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')
                                        }
                                      >
                                        <WebAssetIcon className={classes.selectIcon} />
                                        {option.sourseLabel}
                                        {isGoogleAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify='space-between' style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button variant='outlined' onClick={handleBack} className={classes.submitBtn}>
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button variant='contained' color='primary' onClick={handleNext} className={classes.submitBtn}>
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel>
            <TabPanel value={value} index='two'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='group attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>Google Attributes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('group')
                      .map((attr: any, index: number) => {
                        const fieldConfig = getFieldConfig(attr);
                        // Show row if it has either destKey or sourseKey (for display purposes)
                        if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                              {fieldConfig.destLabel || 'Unknown'}
                              {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                              {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                            </TableCell>
                            <TableCell className={`${classes.border} ${classes.cellLink}`}>
                              {isGoogleAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                                <FormControl fullWidth>
                                  <Select
                                    key={`${fieldConfig.destKey}-${
                                      getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'
                                    }`}
                                    value={getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || ''}
                                    onChange={(e) =>
                                      handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                    }
                                    displayEmpty
                                    className={classes.selectBlock}
                                  >
                                    <MenuItem value=''>None</MenuItem>
                                    {getGoogleAttributesByEntity('group').map((option: any, optIndex: number) => (
                                      <MenuItem
                                        key={optIndex}
                                        value={option.sourseKey}
                                        disabled={
                                          isGoogleAttributeFixed(option.sourseKey) ||
                                          isGoogleAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')
                                        }
                                      >
                                        <WebAssetIcon className={classes.selectIcon} />
                                        {option.sourseLabel}
                                        {isGoogleAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify='space-between' style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button variant='outlined' onClick={handleBack} className={classes.submitBtn}>
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button variant='contained' color='primary' onClick={handleNext} className={classes.submitBtn}>
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel>
            <TabPanel value={value} index='three'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='department attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>Google Attributes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('department')
                      .map((attr: any, index: number) => {
                        const fieldConfig = getFieldConfig(attr);
                        // Show row if it has either destKey or sourseKey (for display purposes)
                        if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                              {fieldConfig.destLabel || 'Unknown'}
                              {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                              {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                            </TableCell>
                            <TableCell className={`${classes.border} ${classes.cellLink}`}>
                              {isGoogleAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                                <FormControl fullWidth>
                                  <Select
                                    key={`${fieldConfig.destKey}-${
                                      getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'
                                    }`}
                                    value={getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || ''}
                                    onChange={(e) =>
                                      handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                    }
                                    displayEmpty
                                    className={classes.selectBlock}
                                  >
                                    <MenuItem value=''>None</MenuItem>
                                    {getGoogleAttributesByEntity('department').map((option: any, optIndex: number) => (
                                      <MenuItem
                                        key={optIndex}
                                        value={option.sourseKey}
                                        disabled={
                                          isGoogleAttributeFixed(option.sourseKey) ||
                                          isGoogleAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')
                                        }
                                      >
                                        <WebAssetIcon className={classes.selectIcon} />
                                        {option.sourseLabel}
                                        {isGoogleAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify='space-between' style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button variant='outlined' onClick={handleBack} className={classes.submitBtn}>
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button variant='contained' color='primary' onClick={handleNext} className={classes.submitBtn}>
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel>
          </div>
        </Paper>
      </Grid>
    </>
  );
});
